import type { NumeralThousandsGroupStyleType } from "cleave.js/options";

import type { MaskConfig } from "./types";

const FLOAT_DECIMALS = 2;
const DOLLAR_DECIMALS = 2;
const DOLLAR_FOUR_DEC_DECIMALS = 4;
const TOTAL_DOLLAR_DECIMALS = 2;

/**
 * float mask without thousands separator (e.g., 12345.67)
 * used for: Quantity in Invoice Line Items
 * @param maxLength - total digits (e.g., 7 for numeric(7,2) = 99999.99)
 */
export const floatMask = (maxLength?: number): MaskConfig => {
  const integerScale = maxLength ? maxLength - FLOAT_DECIMALS : 7;

  return {
    cleaveOptions: {
      numeral: true,
      numeralDecimalScale: FLOAT_DECIMALS,
      numeralDecimalMark: ".",
      numeralThousandsGroupStyle: "none" as NumeralThousandsGroupStyleType,
      numeralPositiveOnly: true,
      numeralIntegerScale: integerScale,
    },
    placeholder: "0.00",
    formatValue: (rawValue) => rawValue,
    decimalPlaces: FLOAT_DECIMALS,
  };
};

/**
 * currency mask with thousands separator (e.g., $1,234.56)
 * used for: Rate in Invoice Line Items
 * @param maxLength - total digits (e.g., 7 for numeric(7,2) = 99,999.99)
 */
export const dollarMask = (maxLength?: number): MaskConfig => {
  const integerScale = maxLength ? maxLength - DOLLAR_DECIMALS : 7;

  return {
    cleaveOptions: {
      numeral: true,
      numeralDecimalScale: DOLLAR_DECIMALS,
      numeralDecimalMark: ".",
      numeralThousandsGroupStyle: "thousand" as NumeralThousandsGroupStyleType,
      numeralPositiveOnly: true,
      numeralIntegerScale: integerScale,
    },
    placeholder: "0.00",
    formatValue: (rawValue) => rawValue,
    decimalPlaces: DOLLAR_DECIMALS,
  };
};

/**
 * currency mask with 4 decimal places
 * used for: Unit Price in allocation pages
 * @param maxLength - total digits (e.g., 14 for numeric(14,4) = 9,999,999,999.9999)
 */
export const dollarFourDecMask = (maxLength?: number): MaskConfig => {
  const integerScale = maxLength ? maxLength - DOLLAR_FOUR_DEC_DECIMALS : 10;

  return {
    cleaveOptions: {
      numeral: true,
      numeralDecimalScale: DOLLAR_FOUR_DEC_DECIMALS,
      numeralDecimalMark: ".",
      numeralThousandsGroupStyle: "thousand" as NumeralThousandsGroupStyleType,
      numeralPositiveOnly: true,
      numeralIntegerScale: integerScale,
    },
    placeholder: "0.0000",
    formatValue: (rawValue) => rawValue,
    decimalPlaces: DOLLAR_FOUR_DEC_DECIMALS,
  };
};

/**
 * currency mask for large totals with no digit limit
 * used for: BE calculated totals that are bigger than typical currency fields
 * note: empty placeholder to differentiate from standard dollar inputs
 */
export const totalDollarMask = (): MaskConfig => ({
  cleaveOptions: {
    numeral: true,
    numeralDecimalScale: TOTAL_DOLLAR_DECIMALS,
    numeralDecimalMark: ".",
    numeralThousandsGroupStyle: "thousand" as NumeralThousandsGroupStyleType,
    numeralPositiveOnly: true,
    // no integerScale = no limit
  },
  placeholder: "", // empty to indicate it's a calculated/read-only field
  formatValue: (rawValue) => rawValue,
  decimalPlaces: TOTAL_DOLLAR_DECIMALS,
});
